import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CommandPalette } from '../../components/ui/CommandPalette';
import { Dialog } from '../../components/ui/Dialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useApp } from '../../lib/app/AppContext';

const nav = [
  ['Overview', '/dashboard', '⌂'],
  ['Analytics', '/dashboard/analytics', '⌁'],
  ['Projects', '/dashboard/projects', '◇'],
  ['Inbox', '/dashboard/inbox', '✉'],
  ['Team', '/dashboard/team', '◎'],
  ['Design system', '/dashboard/design-system', '✦'],
  ['Settings', '/dashboard/settings', '⚙'],
] as const;

function notificationTime(value: string) {
  const date = new Date(value);
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return 'Now';
  if (minutes < 60) return minutes + 'm';
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours + 'h';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function AppLayout() {
  const { pushToast } = useToast();
  const {
    user,
    logout,
    workspaces,
    activeWorkspace,
    switchWorkspace,
    projects,
    messages,
    notifications,
    markNotificationRead,
    markAllNotificationsRead,
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [commandOpen, setCommandOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const closeCommand = useCallback(() => setCommandOpen(false), []);
  const storageKb = (new Blob([localStorage.getItem('gpt-build-app-state-v3') ?? '']).size / 1024).toFixed(1);
  const unreadMessages = messages.filter((message) => message.folder === 'inbox' && message.unread).length;
  const unreadNotifications = notifications.filter((notification) => !notification.read).length;
  const visibleNotifications = useMemo(() => notifications.slice(0, 6), [notifications]);

  useEffect(() => {
    setNotificationsOpen(false);
    setWorkspaceMenuOpen(false);
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((value) => !value);
      }
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
        setWorkspaceMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="app-frame">
      <aside className={sidebarOpen ? 'sidebar open' : 'sidebar'}>
        <div className="brand-lockup">
          <div className="brand-mark">G</div>
          <div><strong>GPT Build</strong><span>Control Center</span></div>
          <button className="mobile-close" onClick={() => setSidebarOpen(false)} aria-label="Close navigation">×</button>
        </div>

        <div className="workspace-switcher">
          <div className="workspace-avatar">GP</div>
          <div><strong>{activeWorkspace.name}</strong><span>{activeWorkspace.region}</span></div>
          <button onClick={() => setWorkspaceMenuOpen((value) => !value)} aria-label="Switch workspace" aria-expanded={workspaceMenuOpen}>⌄</button>
          {workspaceMenuOpen ? (
            <div className="workspace-menu">
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => {
                    switchWorkspace(workspace.id);
                    setWorkspaceMenuOpen(false);
                    pushToast('Workspace switched', { message: workspace.name, tone: 'info' });
                  }}
                >
                  {workspace.name}{workspace.id === activeWorkspace.id ? <b>Active</b> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <nav className="side-nav" aria-label="Main navigation">
          <span className="nav-label">Workspace</span>
          {nav.slice(0, 5).map(([label, path, icon]) => (
            <NavLink to={path} end={path === '/dashboard'} key={path} onClick={() => setSidebarOpen(false)}>
              <i>{icon}</i><span>{label}</span>{label === 'Inbox' && unreadMessages ? <b>{unreadMessages}</b> : null}
            </NavLink>
          ))}
          <span className="nav-label">System</span>
          {nav.slice(5).map(([label, path, icon]) => (
            <NavLink to={path} key={path} onClick={() => setSidebarOpen(false)}>
              <i>{icon}</i><span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-card">
          <div className="sidebar-card-icon">✦</div>
          <strong>Local-first workspace</strong>
          <p>{storageKb} KB persisted in this browser across projects, messages, settings and workspaces.</p>
          <div className="usage-bar"><span style={{ width: Math.min(100, Number(storageKb) / 2) + '%' }} /></div>
          <button onClick={() => setUsageOpen(true)}>View workspace data</button>
        </div>

        <div className="sidebar-user">
          <div className="avatar avatar-md">{user?.name.split(' ').map((part) => part[0]).join('').slice(0, 2) ?? 'DU'}</div>
          <div><strong>{user?.name ?? 'User'}</strong><span>{user?.role ?? 'Viewer'}</span></div>
          <button onClick={() => { logout(); void navigate('/login'); }} title="Sign out" aria-label="Sign out">↪</button>
        </div>
      </aside>

      <div className="content-frame">
        <header className="topbar">
          <button className="mobile-menu" onClick={() => setSidebarOpen(true)} aria-label="Open navigation">☰</button>
          <button className="global-search" onClick={() => setCommandOpen(true)}>
            <span>⌕</span><em>Search workspace or run a command…</em><kbd>⌘ K</kbd>
          </button>
          <div className="topbar-actions">
            <div className="live-pill"><i /> Local workspace ready</div>
            <button
              className="top-icon"
              onClick={() => setNotificationsOpen((value) => !value)}
              aria-label="Notifications"
              aria-expanded={notificationsOpen}
            >
              ♢{unreadNotifications ? <b>{unreadNotifications}</b> : null}
            </button>
            <button className="top-icon" onClick={() => setHelpOpen(true)} aria-label="Help">?</button>
          </div>

          {notificationsOpen ? (
            <div className="notification-popover">
              <div>
                <strong>Notifications</strong>
                <span className="notification-heading-actions">
                  <button
                    disabled={!unreadNotifications}
                    onClick={() => {
                      markAllNotificationsRead();
                      pushToast('Notifications cleared', { message: 'All notifications marked as read.', tone: 'success' });
                    }}
                  >
                    Mark all read
                  </button>
                  <button onClick={() => setNotificationsOpen(false)} aria-label="Close notifications">×</button>
                </span>
              </div>
              {visibleNotifications.length ? visibleNotifications.map((notification) => (
                <button
                  key={notification.id}
                  className={notification.read ? 'notification-item read' : 'notification-item'}
                  onClick={() => markNotificationRead(notification.id)}
                >
                  <i className={'status-dot ' + (notification.tone === 'warning' ? 'warn' : 'ok')} />
                  <span><b>{notification.title}</b><small>{notification.message}</small></span>
                  <time>{notificationTime(notification.createdAt)}</time>
                </button>
              )) : (
                <div className="empty-state compact"><strong>No notifications</strong><span>New application events will appear here.</span></div>
              )}
            </div>
          ) : null}
        </header>

        <main className="app-content"><Outlet /></main>
        <footer className="app-footer">
          <span>GPT Build Control Center</span>
          <span>Commercial SaaS template · React + TypeScript</span>
        </footer>
      </div>

      {sidebarOpen ? <button className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-label="Close navigation" /> : null}
      <CommandPalette open={commandOpen} onClose={closeCommand} />

      <Dialog open={usageOpen} title="Workspace data" onClose={() => setUsageOpen(false)}>
        <div className="detail-grid">
          <span>Local state <b>{storageKb} KB</b></span>
          <span>Projects <b>{projects.length}</b></span>
          <span>Messages <b>{messages.length}</b></span>
          <span>Workspaces <b>{workspaces.length}</b></span>
        </div>
      </Dialog>

      <Dialog open={helpOpen} title="Help & shortcuts" onClose={() => setHelpOpen(false)}>
        <div className="shortcut-list">
          <span><kbd>⌘ K</kbd> Open command palette</span>
          <span><kbd>Esc</kbd> Close transient menus</span>
          <span><b>Persistence</b> Browser local storage</span>
        </div>
      </Dialog>
    </div>
  );
}
